import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import { DeleteAccredited, ProgramAccredited, GetProgramAccreditedPaged, IPaginated } from '@bforms-web/common';

import { useLoading } from '../../../contexts/loading.context';
import AccreditedTable from '../../AccreditedTable';

import { useStyles } from './styles';
import { KeysLocalStorage } from '../../../enums/keys-localstorage';
import { ACCREDITEDS_ROUTE } from '../../../constants';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';

export type ProgramAccreditedListProps = {
  getProgramAccreditedPaged: GetProgramAccreditedPaged;
  deleteAccredited: DeleteAccredited,
};

type ProgramAccreditedListState = {
  programAccredited: IPaginated<ProgramAccredited>;
};

type FilterState = {
  page: number;
  rows: number;
  timeout: number;
};

const defaultState: ProgramAccreditedListState = {
  programAccredited: {
    data: [],
    total: 0,
  },
};

const defaultFilterState: FilterState = {
  page: 0,
  rows: 10,
  timeout: 0,
};

export default function AccreditedList({ getProgramAccreditedPaged, deleteAccredited }: ProgramAccreditedListProps): JSX.Element {
  const classes = useStyles();
  const [state, setState] = useState<ProgramAccreditedListState>(defaultState);
  const [filterState, setFilterState] = useState<FilterState>(defaultFilterState);
  const { showLoading, hideLoading } = useLoading();
  const { push } = useHistory();

  useEffect(() => {
    const handler = setTimeout(() => {
    showLoading();
    (async () => {
      try {
        localStorage.removeItem(KeysLocalStorage.DATA_ACCREDITED);
        const programAccredited = await getProgramAccreditedPaged.execute({
          programId: 'NoProgram', // Caso crie um filtro na tela para buscar programa e credenciado
          filter: 'NoFilter',    // Caso seja implementado algum filtro na tela de credenciados
          skip: filterState.page * filterState.rows,
          take: filterState.rows,
        });
  
        const sortedData = programAccredited.data.sort((a, b) => {
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });

        setState({
          programAccredited: {
            ...programAccredited,
            data: sortedData
          },
        });
      } catch (err) {
        console.log(err);
      } finally {
        hideLoading();
      }
    })();
    }, filterState.timeout);
    return () => clearTimeout(handler);
  }, [filterState]);

  const handlePageChange = useCallback((event: unknown, page: number) => {
    setFilterState(prev => ({
      ...prev,
      page,
      timeout: 200,
    }));
  }, []);

  const handleRowsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newRows = parseInt(event.target.value, 10) || 10;
    setFilterState((prevFilterState) => ({
      ...prevFilterState,
      rows: newRows,
      page: 0,
    }));
  }, []);
  
  const handleRemoveClick = async (selectedProgramAccredited: ProgramAccredited) => {
    showLoading();
    try {
      await deleteAccredited.execute({
        accreditedId: selectedProgramAccredited.accredited.id,
        programId: selectedProgramAccredited.program.id,
      });
  
      toast.success('Credenciado removido com sucesso.');
  
      const programAccredited = await getProgramAccreditedPaged.execute({
        programId: 'NoProgram', // Caso crie um filtro na tela para buscar programa e credenciado
        filter: 'NoFilter',    // Caso seja implementado algum filtro na tela de credenciados
        skip: filterState.page * filterState.rows,
        take: filterState.rows,
      });

      setState({
        programAccredited,
      });

      push(ACCREDITEDS_ROUTE);
    } catch (err) {
      console.log(err);
      toast.error('Falha ao remover credenciado. ' + err.message);
    } finally {
      hideLoading();
    }
  };
     
   return (
     <Box className={classes.container}>
      <AccreditedTable
        count={state.programAccredited.total || 0}
        data={state.programAccredited.data} 
        onChangePage={handlePageChange}
        onChangeRows={handleRowsChange}
        onRemove={handleRemoveClick}
        page={filterState.page}
        rowsPerPage={filterState.rows}
      />
     </Box>
   );
 }